
import { Product, Category, Order, Store, PlatformConfig, PayoutRequest, CoinTransaction, User, AffiliateClick, Review, ReturnRequest, Promotion, SubscriptionBox, UserSubscription, StaffActivity, Influencer } from '../types';
import { MOCK_PRODUCTS, MOCK_CATEGORIES, MOCK_STORE, MOCK_ORDERS } from './mockDb';

const STORAGE_KEYS = {
  PRODUCTS: 'lynqar_products',
  CATEGORIES: 'lynqar_categories',
  ORDERS: 'lynqar_orders',
  STORES: 'lynqar_stores_list',
  SINGLE_STORE_CONFIG: 'lynqar_store_config',
  PLATFORM_CONFIG: 'lynqar_platform_config',
  PAYOUT_REQUESTS: 'lynqar_payout_requests',
  COIN_TRANSACTIONS: 'lynqar_coin_history',
  AUTH_USER: 'lynqar_auth_user',
  AFFILIATE_CLICKS: 'lynqar_affiliate_clicks',
  REVIEWS: 'lynqar_reviews',
  RETURNS: 'lynqar_returns',
  PROMOTIONS: 'lynqar_promotions',
  SUBSCRIPTION_BOXES: 'lynqar_sub_boxes',
  USER_SUBSCRIPTIONS: 'lynqar_user_subs',
  STAFF_ACTIVITY: 'lynqar_staff_audit',
  INFLUENCERS: 'lynqar_influencers'
};

const DEFAULT_PROMOTIONS: Promotion[] = [
  {
    id: 'promo-1',
    title: 'Future Tech. Now.',
    subtitle: 'Explore the next generation of boutique electronics and AI gadgets.',
    imageUrl: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?q=80&w=2070&auto=format&fit=crop',
    linkUrl: '#marketplace-grid',
    type: 'HERO',
    status: 'ACTIVE',
    priority: 1
  },
  {
    id: 'promo-2',
    title: 'Join the Tech Revolution',
    subtitle: 'Scale your business with Lynqar - The world\'s first boutique electronics hub.',
    imageUrl: 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?q=80&w=2070&auto=format&fit=crop',
    linkUrl: '/seller-signup',
    type: 'HERO',
    status: 'ACTIVE',
    priority: 2
  }
];

const DEFAULT_PLATFORM_CONFIG: PlatformConfig = {
  globalCommissionRate: 0.10,
  settlementDays: 7
};

const DEFAULT_SOCIAL_PROOF = {
  enabled: true,
  showRecentSales: true,
  showStockAlerts: true,
  showLiveViewers: true,
  intervalSeconds: 15
};

export const storage = {
  getProducts: (): Product[] => {
    const data = localStorage.getItem(STORAGE_KEYS.PRODUCTS);
    return data ? JSON.parse(data) : MOCK_PRODUCTS;
  },
  saveProducts: (products: Product[]) => {
    localStorage.setItem(STORAGE_KEYS.PRODUCTS, JSON.stringify(products));
  },
  
  getCategories: (): Category[] => {
    const data = localStorage.getItem(STORAGE_KEYS.CATEGORIES);
    return data ? JSON.parse(data) : MOCK_CATEGORIES;
  },
  saveCategories: (categories: Category[]) => {
    localStorage.setItem(STORAGE_KEYS.CATEGORIES, JSON.stringify(categories));
  },

  getOrders: (): Order[] => {
    const data = localStorage.getItem(STORAGE_KEYS.ORDERS);
    return data ? JSON.parse(data) : MOCK_ORDERS;
  },
  saveOrders: (orders: Order[]) => {
    localStorage.setItem(STORAGE_KEYS.ORDERS, JSON.stringify(orders));
  },

  getStores: (): Store[] => {
    const data = localStorage.getItem(STORAGE_KEYS.STORES);
    const stores = data ? JSON.parse(data) : [{ ...MOCK_STORE, status: 'ACTIVE', socialProof: DEFAULT_SOCIAL_PROOF }];
    return Array.isArray(stores) ? stores.map(s => ({ ...s, socialProof: s.socialProof || DEFAULT_SOCIAL_PROOF })) : [stores];
  },

  saveStores: (stores: Store[]) => {
    localStorage.setItem(STORAGE_KEYS.STORES, JSON.stringify(stores));
    const activeUser = localStorage.getItem(STORAGE_KEYS.AUTH_USER);
    if (activeUser) {
      const user = JSON.parse(activeUser);
      const myStore = stores.find(s => s.id === user.storeId);
      if (myStore) localStorage.setItem(STORAGE_KEYS.SINGLE_STORE_CONFIG, JSON.stringify(myStore));
    }
  },
  
  getStoreConfig: (storeId?: string): Store => {
    const stores = storage.getStores();
    if (storeId) {
      return stores.find(s => s.id === storeId) || { ...MOCK_STORE, status: 'ACTIVE', socialProof: DEFAULT_SOCIAL_PROOF };
    }
    const data = localStorage.getItem(STORAGE_KEYS.SINGLE_STORE_CONFIG);
    const config = data ? JSON.parse(data) : { ...MOCK_STORE, status: 'ACTIVE', socialProof: DEFAULT_SOCIAL_PROOF };
    return { ...config, socialProof: config.socialProof || DEFAULT_SOCIAL_PROOF };
  },
  
  saveStoreConfig: (store: Store) => {
    const stores = storage.getStores();
    const index = stores.findIndex(s => s.id === store.id);
    if (index !== -1) {
      stores[index] = store;
    } else {
      stores.push(store);
    }
    localStorage.setItem(STORAGE_KEYS.STORES, JSON.stringify(stores));
    localStorage.setItem(STORAGE_KEYS.SINGLE_STORE_CONFIG, JSON.stringify(store));
  },

  updateStoreStatus: (storeId: string, status: Store['status']) => {
    const stores = storage.getStores();
    const updated = stores.map(s => s.id === storeId ? { ...s, status } : s);
    localStorage.setItem(STORAGE_KEYS.STORES, JSON.stringify(updated));
  },

  getPlatformConfig: (): PlatformConfig => {
    const data = localStorage.getItem(STORAGE_KEYS.PLATFORM_CONFIG);
    return data ? JSON.parse(data) : DEFAULT_PLATFORM_CONFIG;
  },

  savePlatformConfig: (config: PlatformConfig) => {
    localStorage.setItem(STORAGE_KEYS.PLATFORM_CONFIG, JSON.stringify(config));
  },

  getPayoutRequests: (): PayoutRequest[] => {
    const data = localStorage.getItem(STORAGE_KEYS.PAYOUT_REQUESTS);
    return data ? JSON.parse(data) : [];
  },

  savePayoutRequests: (requests: PayoutRequest[]) => {
    localStorage.setItem(STORAGE_KEYS.PAYOUT_REQUESTS, JSON.stringify(requests));
  },

  getCoinTransactions: (userId: string): CoinTransaction[] => {
    const data = localStorage.getItem(STORAGE_KEYS.COIN_TRANSACTIONS);
    const all = data ? JSON.parse(data) : [];
    return all.filter((t: CoinTransaction) => t.userId === userId);
  },

  saveCoinTransaction: (tx: CoinTransaction) => {
    const data = localStorage.getItem(STORAGE_KEYS.COIN_TRANSACTIONS);
    const all = data ? JSON.parse(data) : [];
    all.push(tx);
    localStorage.setItem(STORAGE_KEYS.COIN_TRANSACTIONS, JSON.stringify(all));
    
    const userData = localStorage.getItem(STORAGE_KEYS.AUTH_USER);
    if (userData) {
      const user: User = JSON.parse(userData);
      if (user.id === tx.userId) {
        user.coins = (user.coins || 0) + tx.amount;
        localStorage.setItem(STORAGE_KEYS.AUTH_USER, JSON.stringify(user));
        window.dispatchEvent(new Event('auth-change'));
      }
    }
  },

  getAffiliateClicks: (storeId?: string): AffiliateClick[] => {
    const data = localStorage.getItem(STORAGE_KEYS.AFFILIATE_CLICKS);
    const all: AffiliateClick[] = data ? JSON.parse(data) : [];
    return storeId ? all.filter(c => c.storeId === storeId) : all;
  },

  saveAffiliateClick: (click: AffiliateClick) => {
    const data = localStorage.getItem(STORAGE_KEYS.AFFILIATE_CLICKS);
    const all = data ? JSON.parse(data) : [];
    all.push(click);
    localStorage.setItem(STORAGE_KEYS.AFFILIATE_CLICKS, JSON.stringify(all));
  },

  getReviews: (productId?: string): Review[] => {
    const data = localStorage.getItem(STORAGE_KEYS.REVIEWS);
    const all: Review[] = data ? JSON.parse(data) : [];
    return productId ? all.filter(r => r.productId === productId) : all;
  },

  saveReview: (review: Review) => {
    const all = storage.getReviews();
    const updated = [review, ...all];
    localStorage.setItem(STORAGE_KEYS.REVIEWS, JSON.stringify(updated));

    // Update product rating stats
    const products = storage.getProducts();
    const product = products.find(p => p.id === review.productId);
    if (product) {
      const productReviews = updated.filter(r => r.productId === review.productId);
      const totalRating = productReviews.reduce((sum, r) => sum + r.rating, 0);
      product.rating = totalRating / productReviews.length;
      product.reviewCount = productReviews.length;
      storage.saveProducts(products);
    }
  },

  getReturns: (storeId?: string): ReturnRequest[] => {
    const data = localStorage.getItem(STORAGE_KEYS.RETURNS);
    const all: ReturnRequest[] = data ? JSON.parse(data) : [];
    return storeId ? all.filter(r => r.storeId === storeId) : all;
  },

  saveReturnRequest: (req: ReturnRequest) => {
    const all = storage.getReturns();
    const existing = all.findIndex(r => r.id === req.id);
    if (existing !== -1) {
      all[existing] = req;
    } else {
      all.push(req);
    }
    localStorage.setItem(STORAGE_KEYS.RETURNS, JSON.stringify(all));
  },

  getPromotions: (): Promotion[] => {
    const data = localStorage.getItem(STORAGE_KEYS.PROMOTIONS);
    return data ? JSON.parse(data) : DEFAULT_PROMOTIONS;
  },

  savePromotions: (promos: Promotion[]) => {
    localStorage.setItem(STORAGE_KEYS.PROMOTIONS, JSON.stringify(promos));
  },

  getSubscriptionBoxes: (storeId?: string): SubscriptionBox[] => {
    const data = localStorage.getItem(STORAGE_KEYS.SUBSCRIPTION_BOXES);
    const all: SubscriptionBox[] = data ? JSON.parse(data) : [];
    return storeId ? all.filter(b => b.storeId === storeId) : all;
  },

  saveSubscriptionBox: (box: SubscriptionBox) => {
    const all = storage.getSubscriptionBoxes();
    const idx = all.findIndex(b => b.id === box.id);
    if (idx !== -1) all[idx] = box;
    else all.push(box);
    localStorage.setItem(STORAGE_KEYS.SUBSCRIPTION_BOXES, JSON.stringify(all));
  },

  getUserSubscriptions: (userId: string): UserSubscription[] => {
    const data = localStorage.getItem(STORAGE_KEYS.USER_SUBSCRIPTIONS);
    const all: UserSubscription[] = data ? JSON.parse(data) : [];
    const boxes = storage.getSubscriptionBoxes();
    return all
      .filter(s => s.userId === userId)
      .map(s => ({
        ...s,
        box: boxes.find(b => b.id === s.boxId)
      }));
  },

  saveUserSubscription: (sub: UserSubscription) => {
    const data = localStorage.getItem(STORAGE_KEYS.USER_SUBSCRIPTIONS);
    const all: UserSubscription[] = data ? JSON.parse(data) : [];
    const idx = all.findIndex(s => s.id === sub.id);
    if (idx !== -1) all[idx] = sub;
    else all.push(sub);
    localStorage.setItem(STORAGE_KEYS.USER_SUBSCRIPTIONS, JSON.stringify(all));
  },

  getStaffActivity: (storeId: string): StaffActivity[] => {
    const data = localStorage.getItem(STORAGE_KEYS.STAFF_ACTIVITY);
    const all: (StaffActivity & { storeId: string })[] = data ? JSON.parse(data) : [];
    return all.filter(a => a.storeId === storeId);
  },

  logStaffActivity: (storeId: string, activity: Omit<StaffActivity, 'id' | 'timestamp'>) => {
    const data = localStorage.getItem(STORAGE_KEYS.STAFF_ACTIVITY);
    const all: (StaffActivity & { storeId: string })[] = data ? JSON.parse(data) : [];
    all.push({
      ...activity,
      id: 'act-' + Date.now(),
      timestamp: new Date().toISOString(),
      storeId
    });
    localStorage.setItem(STORAGE_KEYS.STAFF_ACTIVITY, JSON.stringify(all.slice(-50))); // Keep last 50
  },

  getInfluencers: (storeId: string): Influencer[] => {
    const data = localStorage.getItem(STORAGE_KEYS.INFLUENCERS);
    const all: Influencer[] = data ? JSON.parse(data) : [];
    return all.filter(i => i.storeId === storeId);
  },

  saveInfluencer: (influencer: Influencer) => {
    const data = localStorage.getItem(STORAGE_KEYS.INFLUENCERS);
    const all: Influencer[] = data ? JSON.parse(data) : [];
    const idx = all.findIndex(i => i.id === influencer.id);
    if (idx !== -1) all[idx] = influencer;
    else all.push(influencer);
    localStorage.setItem(STORAGE_KEYS.INFLUENCERS, JSON.stringify(all));
  }
};
